"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useTaskViewColumnConfigsManager = void 0;
const react_1 = require("react");
const task_view_column_config_key_util_1 = require("./task-view-column-config-key.util");
const task_view_column_configs_storage_1 = require("./task-view-column-configs.storage");
const query_params_1 = require("../../../hooks/query-params");
const hooks_1 = require("../../../../hooks");
const createDefaultConfig = () => ({
    columnsOrder: [],
    excludedColumns: [],
    filterOptions: [],
    selectAllMode: false,
    reviewAttribute: undefined,
});
/**
 * Bridges the localStorage-backed task view column configs store to React.
 *
 * Resolves the current `project–catalog–node–attribute` record from the page query
 * params, exposes its persisted config, and write-throughs every change to storage
 * (which enforces the records cap). Designed to plug directly into
 * `useCustomizeTableColumns({ persisted: true, options: { ... } })`.
 *
 * When the resolved record changes (e.g. switching node or attribute), the active
 * config is re-read so each record keeps its own layout.
 */
const useTaskViewColumnConfigsManager = () => {
    const ls = (0, hooks_1.useLocalStorage)();
    const { projectId, catalogId, terminalNodeId, attributeName, } = (0, query_params_1.useQueryParams)();
    const key = (0, task_view_column_config_key_util_1.buildTaskViewColumnConfigKey)({
        projectId,
        catalogId,
        nodeId: terminalNodeId,
        attributeName,
    });
    const [config, setConfig] = (0, react_1.useState)(() => {
        var _a;
        const stored = (_a = (key ? (0, task_view_column_configs_storage_1.readTaskViewColumnConfig)(ls, key) : undefined)) !== null && _a !== void 0 ? _a : createDefaultConfig();
        return stored;
    });
    const [hasStoredConfig, setHasStoredConfig] = (0, react_1.useState)(() => Boolean(key && (0, task_view_column_configs_storage_1.readTaskViewColumnConfig)(ls, key)));
    const [hadStoredConfigOnLoad, setHadStoredConfigOnLoad] = (0, react_1.useState)(() => Boolean(key && (0, task_view_column_configs_storage_1.readTaskViewColumnConfig)(ls, key)));
    const configRef = (0, react_1.useRef)(config);
    (0, react_1.useEffect)(() => {
        const stored = key ? (0, task_view_column_configs_storage_1.readTaskViewColumnConfig)(ls, key) : undefined;
        const next = stored !== null && stored !== void 0 ? stored : createDefaultConfig();
        configRef.current = next;
        setConfig(next);
        setHasStoredConfig(Boolean(stored));
        setHadStoredConfigOnLoad(Boolean(stored));
    }, [key, ls]);
    const setPersistedTableData = (0, react_1.useCallback)((changes) => {
        const next = Object.assign(Object.assign({}, configRef.current), changes);
        configRef.current = next;
        setConfig(next);
        if (key) {
            (0, task_view_column_configs_storage_1.upsertTaskViewColumnConfig)(ls, key, next);
            setHasStoredConfig(true);
        }
    }, [key, ls]);
    return {
        persistedTableData: config,
        setPersistedTableData,
        hasStoredConfig,
        hadStoredConfigOnLoad,
    };
};
exports.useTaskViewColumnConfigsManager = useTaskViewColumnConfigsManager;
